import { type FC, type ReactNode, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { siteMetadata } from '@/config/seo';

type StructuredData = Record<string, unknown> | Record<string, unknown>[];

export interface SeoProps {
  title?: string;
  description?: string;
  canonicalPath?: string;
  image?: string;
  imageAlt?: string;
  imageWidth?: number;
  imageHeight?: number;
  imageType?: string;
  type?: 'website' | 'article' | 'profile' | 'product';
  keywords?: string[];
  noIndex?: boolean;
  publishedTime?: string;
  modifiedTime?: string;
  structuredData?: StructuredData;
  children?: ReactNode;
}

const buildAbsoluteUrl = (input?: string): string | undefined => {
  if (!input) return undefined;
  if (/^https?:\/\//i.test(input)) return input;
  const normalized = input.startsWith('/') ? input : `/${input}`;
  return `${siteMetadata.siteUrl}${normalized}`;
};

const inferMimeTypeFromPath = (input?: string): string | undefined => {
  if (!input) return undefined;
  const sanitized = input.split('?')[0]?.split('#')[0] ?? input;
  const extension = sanitized.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'webp':
      return 'image/webp';
    case 'gif':
      return 'image/gif';
    case 'svg':
      return 'image/svg+xml';
    default:
      return undefined;
  }
};

const ensureHttps = (input?: string): string | undefined => {
  if (!input) return undefined;
  return input.replace(/^http:\/\//i, 'https://');
};

const DEFAULT_SOCIAL_WIDTH = 1200;
const DEFAULT_SOCIAL_HEIGHT = 630;

export const Seo: FC<SeoProps> = ({
  title,
  description,
  canonicalPath,
  image,
  imageAlt,
  imageWidth,
  imageHeight,
  imageType,
  type = 'website',
  keywords,
  noIndex,
  publishedTime,
  modifiedTime,
  structuredData,
  children,
}) => {
  const location = useLocation();
  const structuredDataEntries = useMemo(() => {
    if (!structuredData) return [] as Record<string, unknown>[];
    return Array.isArray(structuredData) ? structuredData : [structuredData];
  }, [structuredData]);

  const canonicalUrl = useMemo(() => {
    const path = canonicalPath ?? location.pathname;
    return buildAbsoluteUrl(path || '/');
  }, [canonicalPath, location.pathname]);

  const fullTitle = useMemo(() => {
    if (!title) return siteMetadata.defaultTitle;
    if (siteMetadata.titleTemplate.includes('%s')) {
      return siteMetadata.titleTemplate.replace('%s', title);
    }
    return `${title} | ${siteMetadata.siteName}`;
  }, [title]);

  const metaDescription = description ?? siteMetadata.defaultDescription;
  const fallbackImage = image ?? siteMetadata.defaultImage;
  const imageUrl = buildAbsoluteUrl(fallbackImage);
  const resolvedImageType = image ? imageType ?? inferMimeTypeFromPath(image) : inferMimeTypeFromPath(fallbackImage);
  const secureImageUrl = ensureHttps(imageUrl);
  const resolvedImageWidth = imageWidth ?? DEFAULT_SOCIAL_WIDTH;
  const resolvedImageHeight = imageHeight ?? DEFAULT_SOCIAL_HEIGHT;
  const metaKeywords = useMemo(() => {
    const combined = keywords?.length
      ? Array.from(new Set([...keywords, ...siteMetadata.defaultKeywords]))
      : siteMetadata.defaultKeywords;
    return combined.join(', ');
  }, [keywords]);

  const robotsValue = noIndex ? 'noindex, nofollow' : 'index, follow';

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="robots" content={robotsValue} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteMetadata.siteName} />
      <meta property="og:locale" content={siteMetadata.locale} />
      {imageUrl && <meta property="og:image" content={imageUrl} />}
      {secureImageUrl && <meta property="og:image:secure_url" content={secureImageUrl} />}
      {resolvedImageType && <meta property="og:image:type" content={resolvedImageType} />}
      {resolvedImageWidth && <meta property="og:image:width" content={String(resolvedImageWidth)} />}
      {resolvedImageHeight && <meta property="og:image:height" content={String(resolvedImageHeight)} />}
      <meta
        property="og:image:alt"
        content={imageAlt ?? title ?? siteMetadata.defaultImageAlt}
      />
      {modifiedTime && (
        <meta property="og:updated_time" content={modifiedTime} />
      )}

      <meta name="twitter:card" content="summary_large_image" />
      {canonicalUrl && <meta name="twitter:url" content={canonicalUrl} />}
      {siteMetadata.twitterHandle && (
        <meta name="twitter:site" content={siteMetadata.twitterHandle} />
      )}
      {siteMetadata.twitterHandle && (
        <meta name="twitter:creator" content={siteMetadata.twitterHandle} />
      )}
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      {imageUrl && <meta name="twitter:image" content={imageUrl} />}
      <meta
        name="twitter:image:alt"
        content={imageAlt ?? title ?? siteMetadata.defaultImageAlt}
      />

      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}

      {structuredDataEntries.map((entry, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(entry)}
        </script>
      ))}

      {children}
    </Helmet>
  );
};

export default Seo;
